import {
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanySupplierLetter } from './fetchCompanySupplierLetter';
import { type SupplierLetterMeta } from './types';

/**
 * Query options for getting the supplier letter for a Company, for use in
 * route loaders and prefetching as well as in `useQueryCompanySupplierLetter`.
 * Resolves to `null` when no letter is configured (404).
 *
 * @param companyId - The ID of the company. The query is skipped while it is `undefined`.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_supplier_letter_vadmin_companies__company_id__supplier_letter_get
 */
export const getCompanySupplierLetterQueryOptions = ({
  companyId,
}: {
  companyId: UUID | undefined;
}) =>
  queryOptions<SupplierLetterMeta | null>({
    queryFn: companyId
      ? async () => fetchCompanySupplierLetter(companyId)
      : skipToken,
    queryKey: companyId
      ? queryKeysCompany.getSupplierLetter(companyId)
      : queryKeysCompany.getSupplierLetters(),
    staleTime: ms('1h'),
  });

/**
 * React Query based custom hook for getting the supplier letter for a Company.
 * Resolves to `null` when no letter is configured (404).
 *
 * @param companyId - The ID of the company.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_supplier_letter_vadmin_companies__company_id__supplier_letter_get
 */
export const useQueryCompanySupplierLetter = (
  companyId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<SupplierLetterMeta | null>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getCompanySupplierLetterQueryOptions({ companyId }),
    ...options,
  });
